import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { When } from 'react-if';

import cn from '@/lib/clsxm';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import Loading from '@/components/@base/loading';
import { LOADING_COLORS } from '@/components/@base/loading/type';
import { showToast } from '@/components/@base/toast/show-toast';
import XImage from '@/components/@base/x-image';
import { useChangeQuantityInCart } from '@/components/app/cart/services/hooks';

import {
  DATA_CY_CART_BUTTON,
  DATA_CY_DECREASE_NUMBER_BUTTON,
  DATA_CY_INCREASE_NUMBER_BUTTON,
  DATA_CY_QUANTITY_CONTROLS_SECTION,
} from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { GET_CART } from '@/constant/query-key';

interface QuantityControlsProps {
  id: string;
  initialQuantity: number;
  maxLimit: number;
  hideAddon?: boolean;
  isMobile?: boolean;
}

export default function QuantityControls({
  id,
  initialQuantity,
  maxLimit,
  hideAddon,
  isMobile = false,
}: QuantityControlsProps) {
  const [quantity, setQuantity] = useState(initialQuantity || 1);
  const queryClient = useQueryClient();
  const { mutate, isPending } = useChangeQuantityInCart();

  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (
          itemId: string,
          newQuantity: number,
          currentQuantityBeforeOptimisticUpdate: number,
        ) => {
          mutate(
            { id: itemId, quantity: newQuantity },
            {
              onSuccess: (res) => {
                if (res.payload.appliedCodeSteppedDiscount) {
                  showToast({
                    message: res.payload.appliedCodeSteppedDiscount,
                    type: 'default',
                  });
                }
                queryClient.setQueryData([GET_CART], res.payload);
              },
              onError: (error) => {
                setQuantity(currentQuantityBeforeOptimisticUpdate);
                console.error('Error updating quantity:', error);
              },
            },
          );
        },
        300,
      ),
    [mutate, queryClient, hideAddon],
  );

  const handleIncrease = () => {
    const newQuantity = quantity + 1;
    if (newQuantity > maxLimit) return;

    setQuantity(newQuantity);
    debouncedUpdate(id, newQuantity, quantity);
  };

  const handleDecrease = () => {
    const newQuantity = quantity - 1;
    if (newQuantity < 1) return;

    setQuantity(newQuantity);
    debouncedUpdate(id, newQuantity, quantity);
  };

  const isIncreaseDisabled = quantity >= maxLimit;
  const isDecreaseDisabled = quantity === 1;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  return (
    <Container
      data-cy={DATA_CY_QUANTITY_CONTROLS_SECTION}
      className={cn(
        'relative flex h-[25px] w-[50px] items-center gap-2 rounded-[6px] border border-[#E5E8EB] px-0.5 py-1 text-[#7F84F7]',
        isMobile ? 'md:hidden' : 'hidden flex-row! justify-between gap-0.5 md:inline-flex',
      )}
    >
      <Button
        disabled={isIncreaseDisabled}
        onClick={handleIncrease}
        className={cn(
          'text-md bg-transparent! px-0 text-[#7F84F7]',
          'disabled:cursor-default! disabled:text-[#7F84F7]!',
          isIncreaseDisabled && 'opacity-50',
        )}
      >
        <XImage
          src={imagesList.icons.add}
          alt='minus'
          data-cy={DATA_CY_INCREASE_NUMBER_BUTTON}
          width={14}
        />
      </Button>
      <span className='text-sm text-[#7F84F7]'>{quantity}</span>
      <Button
        disabled={isDecreaseDisabled}
        onClick={handleDecrease}
        className={cn(
          'bg-transparent! px-0 text-[#7F84F7]',
          'disabled:cursor-default! disabled:text-[#7F84F7]!',
          isDecreaseDisabled && 'opacity-50',
        )}
      >
        <XImage
          src={imagesList.icons.minus}
          alt='minus'
          data-cy={DATA_CY_DECREASE_NUMBER_BUTTON}
          width={14}
        />
      </Button>
      <When condition={isPending}>
        <Container
          center
          className='absolute top-0 left-0 z-40! h-[24px] w-[49px] rounded-[6px] bg-[#7F84F7]/15'
        >
          <Loading className='h-7 w-7' color={LOADING_COLORS.BLUE} />
        </Container>
      </When>
    </Container>
  );
}
